// src/features/analytics/ReportsTab.tsx
// Reports list + generate dialog + detail rail. ListReports, GenerateReport,
// GetReport, DeleteReport.

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Button, Badge, Alert, Input, Textarea } from '@react/components';
import { Select } from '@/components/Select';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { analyticsApi } from '@core/api';
import type { AvailableDataSource, DataSourceConfig, Report, ReportFormat, ReportType } from '@core/types/analytics';
import { AnalyticsModal } from './Modal';
import { DataSourceConfigEditor } from './DataSourceConfigEditor';
import { errMessage, formatBytes, formatDate, listParams, makeEntity, REPORT_FORMATS, REPORT_TYPES, type ScopeIds } from './helpers';

const EMPTY_DATA_SOURCE: DataSourceConfig = {
  product_slug: '',
  entity_type: '',
  metrics: [],
  filters: [],
  group_by: [],
  order_by: '',
  order_direction: 'desc',
  limit: 0,
};

function statusVariant(status?: string): 'default' | 'success' | 'warning' | 'error' | 'secondary' {
  switch (status) {
    case 'REPORT_STATUS_READY':
      return 'success';
    case 'REPORT_STATUS_FAILED':
      return 'error';
    case 'REPORT_STATUS_GENERATING':
    case 'REPORT_STATUS_QUEUED':
      return 'warning';
    case 'REPORT_STATUS_EXPIRED':
      return 'secondary';
    default:
      return 'default';
  }
}

interface ReportsTabProps {
  scope: ScopeIds;
  dataSources: AvailableDataSource[];
}

export function ReportsTab({ scope, dataSources }: ReportsTabProps) {
  const { t, locale } = useI18n();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Report | null>(null);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Generate form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [reportType, setReportType] = useState<ReportType>('REPORT_TYPE_TABULAR');
  const [format, setFormat] = useState<ReportFormat>('REPORT_FORMAT_CSV');
  const [dataSourceConfigs, setDataSourceConfigs] = useState<DataSourceConfig[]>([EMPTY_DATA_SOURCE]);
  const [saving, setSaving] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    analyticsApi
      .ListReports(listParams({ limit: 50 }))
      .then((res) => setReports(res.items?.items ?? []))
      .catch((e: unknown) => setError(errMessage(e, t('analytics.reports.loadError'))))
      .finally(() => setLoading(false));
  };

  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openGenerate = () => {
    setName('');
    setDescription('');
    setReportType('REPORT_TYPE_TABULAR');
    setFormat('REPORT_FORMAT_CSV');
    setDataSourceConfigs([EMPTY_DATA_SOURCE]);
    setGenError(null);
    setGenerateOpen(true);
  };

  const generate = async () => {
    if (!name.trim()) {
      setGenError(t('analytics.reports.nameRequired'));
      return;
    }
    setSaving(true);
    setGenError(null);
    try {
      const res = await analyticsApi.GenerateReport({
        entity: makeEntity('', 'report', scope),
        name: name.trim(),
        description,
        report_type: reportType,
        format,
        data_sources: dataSourceConfigs,
      });
      const created = res.report;
      if (created) setReports((prev) => [created, ...prev]);
      setGenerateOpen(false);
    } catch (e) {
      setGenError(errMessage(e, t('analytics.reports.generateError')));
    } finally {
      setSaving(false);
    }
  };

  const openDetail = (id: string) => {
    analyticsApi
      .GetReport(id)
      .then((res) => setSelected(res.report ?? null))
      .catch((e: unknown) => setError(errMessage(e, t('analytics.reports.loadError'))));
  };

  const handleDelete = async () => {
    if (!selected?.entity?.id) return;
    setDeleting(true);
    try {
      await analyticsApi.DeleteReport(selected.entity.id);
      setReports((prev) => prev.filter((r) => r.entity?.id !== selected.entity?.id));
      setSelected(null);
    } catch (e) {
      setError(errMessage(e, t('analytics.reports.deleteError')));
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div className="flex gap-4">
      <div className="min-w-0 flex-1 space-y-3">
        <div className="flex items-center justify-end">
          <Button size="sm" onClick={openGenerate}>
            {t('analytics.reports.generate')}
          </Button>
        </div>

        {error && <Alert variant="error">{error}</Alert>}

        {loading && (
          <div className="space-y-2" aria-busy="true">
            <div className="h-14 animate-pulse rounded-md bg-muted" />
            <div className="h-14 animate-pulse rounded-md bg-muted" />
          </div>
        )}

        {!loading && reports.length === 0 && !error && (
          <div className="rounded-md border border-dashed border-border p-8 text-center">
            <p className="text-sm text-muted-foreground">{t('analytics.reports.empty')}</p>
          </div>
        )}

        <div className="space-y-2">
          {reports.map((r) => (
            <button
              key={r.entity?.id}
              onClick={() => r.entity?.id && openDetail(r.entity.id)}
              className={`w-full rounded-md border p-3 text-left hover:bg-muted ${selected?.entity?.id === r.entity?.id ? 'border-primary' : 'border-border'}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-foreground">{r.name}</span>
                <Badge variant={statusVariant(r.status)}>{t(`analytics.reportStatus.${r.status}`)}</Badge>
              </div>
              <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
                <span>{t(`analytics.reportType.${r.report_type}`)}</span>
                <span>{r.format}</span>
                <span>{formatDate(r.created_at, locale)}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {selected && (
        <aside className="w-80 flex-shrink-0 space-y-3 rounded-md border border-border p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">{selected.name}</h3>
            <button onClick={() => setSelected(null)} aria-label={t('common.close')} className="rounded-md p-1 text-muted-foreground hover:bg-muted">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <Badge variant={statusVariant(selected.status)}>{t(`analytics.reportStatus.${selected.status}`)}</Badge>
          {selected.description && <p className="text-sm text-muted-foreground">{selected.description}</p>}
          <dl className="space-y-1 text-xs text-muted-foreground">
            <div className="flex justify-between">
              <dt>{t('analytics.reports.format')}</dt>
              <dd className="text-foreground">{selected.format}</dd>
            </div>
            <div className="flex justify-between">
              <dt>{t('analytics.reports.size')}</dt>
              <dd className="text-foreground">{formatBytes(selected.file_size_bytes)}</dd>
            </div>
          </dl>
          {selected.status === 'REPORT_STATUS_READY' && selected.download_url && (
            <a href={selected.download_url} target="_blank" rel="noreferrer" className="block">
              <Button size="sm" className="w-full">
                {t('analytics.reports.download')}
              </Button>
            </a>
          )}
          <Button size="sm" variant="ghost" className="w-full" onClick={() => setConfirmDelete(true)}>
            {t('common.delete')}
          </Button>
        </aside>
      )}

      <AnalyticsModal
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        title={t('analytics.reports.generate')}
        widthClass="max-w-3xl"
        footer={
          <>
            <Button variant="ghost" onClick={() => setGenerateOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={generate} disabled={saving}>
              {saving ? t('common.saving') : t('analytics.reports.generate')}
            </Button>
          </>
        }
      >
        {genError && (
          <Alert variant="error" className="mb-3">
            {genError}
          </Alert>
        )}
        <div className="space-y-4">
          <Input label={t('analytics.reports.name')} value={name} onChange={(e) => setName(e.target.value)} required />
          <Textarea label={t('analytics.reports.description')} value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          <div className="grid grid-cols-2 gap-3">
            <Select label={t('analytics.reports.type')} value={reportType} onChange={(e) => setReportType(e.target.value as ReportType)}>
              {REPORT_TYPES.map((rt) => (
                <option key={rt} value={rt}>
                  {t(`analytics.reportType.${rt}`)}
                </option>
              ))}
            </Select>
            <Select label={t('analytics.reports.format')} value={format} onChange={(e) => setFormat(e.target.value as ReportFormat)}>
              {REPORT_FORMATS.map((f) => (
                <option key={f} value={f}>
                  {f.replace('REPORT_FORMAT_', '')}
                </option>
              ))}
            </Select>
          </div>
          {dataSourceConfigs.map((ds, i) => (
            <div key={i} className="rounded-md border border-border p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">
                  {t('analytics.reports.dataSource')} {i + 1}
                </span>
                {dataSourceConfigs.length > 1 && (
                  <Button size="sm" variant="ghost" onClick={() => setDataSourceConfigs(dataSourceConfigs.filter((_, idx) => idx !== i))}>
                    {t('common.remove')}
                  </Button>
                )}
              </div>
              <DataSourceConfigEditor
                value={ds}
                onChange={(next) => setDataSourceConfigs(dataSourceConfigs.map((d, idx) => (idx === i ? next : d)))}
                dataSources={dataSources}
              />
            </div>
          ))}
          <Button size="sm" variant="secondary" onClick={() => setDataSourceConfigs([...dataSourceConfigs, EMPTY_DATA_SOURCE])}>
            {t('analytics.reports.addDataSource')}
          </Button>
        </div>
      </AnalyticsModal>

      <ConfirmDialog
        open={confirmDelete}
        title={t('analytics.reports.deleteConfirmTitle')}
        description={t('analytics.reports.deleteConfirmBody', { name: selected?.name ?? '' })}
        destructive
        isConfirming={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
